import { isAdmin } from '@/constants/roles';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
    type IThread,
    threadHistoryState,
    useApi,
    useChatMessages
} from '@chainlit/react-client';

import { useAuth } from '@/hooks/useAuth';

import { DeleteThreadDialog } from './DMDeleteThreadDialog';
import { RenameThreadDialog } from './DMRenameThreadDialog';
import { ThreadActionsMenu } from './DMThreadActionsMenu';
import { useThreadActions } from './useThreadActions';
import { useThreadRename } from './useThreadRename';

export default function DMThreadNameHeader() {
    const location = useLocation();
    const { id: urlThreadId } = useParams();
    const { user } = useAuth();
    const { threadId: chatThreadId } = useChatMessages();
    const threadHistory = useRecoilValue(threadHistoryState);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const isOnThreadRoute = location.pathname.startsWith('/thread');
    const resolvedThreadId = isOnThreadRoute
        ? urlThreadId || chatThreadId
        : undefined;

    useEffect(() => {
        setDeleteDialogOpen(false);
    }, [resolvedThreadId]);

    const historyThread = useMemo(() => {
        return threadHistory?.threads?.find((t) => t.id === resolvedThreadId);
    }, [threadHistory, resolvedThreadId]);

    const { data: apiThread, mutate: mutateApiThread } = useApi<IThread>(
        resolvedThreadId && !historyThread
            ? `/project/thread/${resolvedThreadId}`
            : null,
        { revalidateOnFocus: false }
    );

    const currentThread = historyThread || apiThread;
    const isPinned = currentThread?.isPinned === true;
    const userIsAdmin = useMemo(
        () => isAdmin(user?.metadata?.organization_roles),
        [user]
    );

    const isOwnThread =
        currentThread?.userIdentifier?.toLowerCase() ===
        user?.identifier?.toLowerCase();

    const canEditThread = !isPinned || userIsAdmin;
    const canDeleteThread = isOwnThread && !isPinned;

    const {
        isEditing,
        editValue,
        setEditValue,
        inputRef,
        threadName,
        startEditing,
        saveRename,
        handleKeyDown,
        renameThread
    } = useThreadRename({
        threadId: resolvedThreadId,
        thread: currentThread,
        historyThread,
        mutateApiThread
    });

    const { handlePinToggle, handleDelete } = useThreadActions({
        threadId: resolvedThreadId,
        isPinned,
        canDeleteThread,
        mutateApiThread
    });

    if (!threadName) return null;

    return (
        <>
            <div className="flex items-center gap-0.5">
                {isEditing ? (
                    <input
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveRename}
                        onKeyDown={handleKeyDown}
                        className="bg-transparent px-2 border border-border rounded-md outline-hidden focus:ring-1 focus:ring-ring h-8 font-medium text-base"
                    />
                ) : canEditThread ? (
                    <ThreadActionsMenu
                        threadId={resolvedThreadId}
                        threadName={threadName}
                        isPinned={isPinned}
                        isAdmin={userIsAdmin}
                        canDelete={canDeleteThread}
                        onInlineRename={startEditing}
                        onDialogRename={() => setRenameDialogOpen(true)}
                        onPinToggle={handlePinToggle}
                        onDelete={() => setDeleteDialogOpen(true)}
                    />
                ) : (
                    <span className="px-2 font-medium text-base">
                        {threadName}
                    </span>
                )}
            </div>
            <RenameThreadDialog
                open={renameDialogOpen}
                onOpenChange={setRenameDialogOpen}
                currentName={threadName}
                onConfirm={renameThread}
            />
            <DeleteThreadDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={async () => {
                    setDeleteDialogOpen(false);
                    await handleDelete();
                }}
            />
        </>
    );
}
